export interface TeamProfile {
  abbr: string;
  city: string;
  name: string;
  lastSeasonRecord: string; // e.g. "13-4"
  lastSeasonFinish: string; // e.g. "Won AFC East, Divisional Round"
  expectations2026: string; // Season outlook & win expectations
  bigPlayerMovements: string[]; // Key offseason trades, signings, departures
  offensiveOutlook: string;
  defensiveOutlook: string;
  headCoach: string;
  keyPlaymakers: string[];
}

export const TEAM_PROFILES: Record<string, TeamProfile> = {
  BUF: {
    abbr: 'BUF',
    city: 'Buffalo',
    name: 'Bills',
    lastSeasonRecord: '13-4',
    lastSeasonFinish: '1st in AFC East · AFC Divisional Round',
    expectations2026: 'Super Bowl contender and perennial AFC powerhouse. High win floor anchored by Josh Allen\'s unmatched dual-threat production.',
    bigPlayerMovements: [
      'Traded Stefon Diggs to Houston; revamped wide receiver corps',
      'Drafted explosive rookie WR Keon Coleman in Round 2',
      'Added versatile WR Curtis Samuel and extended LT Dion Dawkins',
      'Key departures: S Jordan Poyer, S Micah Hyde, CB Tre\'Davious White',
    ],
    offensiveOutlook: 'Josh Allen leads a high-efficiency run-heavy spread offense featuring RB James Cook and TE Dalton Kincaid.',
    defensiveOutlook: 'Sean McDermott schemes a disciplined turnover-focused zone defense; healthy Matt Milano and Ed Oliver.',
    headCoach: 'Sean McDermott (OC: Joe Brady)',
    keyPlaymakers: ['Josh Allen (QB)', 'James Cook (RB)', 'Dalton Kincaid (TE)', 'Ed Oliver (DT)'],
  },
  MIA: {
    abbr: 'MIA',
    city: 'Miami',
    name: 'Dolphins',
    lastSeasonRecord: '8-9',
    lastSeasonFinish: '2nd in AFC East',
    expectations2026: 'Electric high-octane offense capable of beating anyone when healthy. Favorable early schedule.',
    bigPlayerMovements: [
      'Signed Tua Tagovailoa to massive multi-year extension',
      'Signed veteran pass-rusher Calais Campbell and S Jordan Poyer',
      'Drafted dynamic edge-rusher Chop Robinson',
      'Key departures: DT Christian Wilkins (to Raiders), OG Robert Hunt',
    ],
    offensiveOutlook: 'Mike McDaniel\'s track-star offense with Tyreek Hill, Jaylen Waddle, and De\'Von Achane.',
    defensiveOutlook: 'New DC Anthony Weaver transitions to a physical front; pass-rush health is critical.',
    headCoach: 'Mike McDaniel (DC: Anthony Weaver)',
    keyPlaymakers: ['Tyreek Hill (WR)', 'Tua Tagovailoa (QB)', 'Jaylen Waddle (WR)', 'Jalen Ramsey (CB)'],
  },
  NE: {
    abbr: 'NE',
    city: 'New England',
    name: 'Patriots',
    lastSeasonRecord: '4-13',
    lastSeasonFinish: '4th in AFC East',
    expectations2026: 'Rebuilding era with a strong defensive floor and high-upside rookie QB Drake Maye.',
    bigPlayerMovements: [
      'Drafted franchise QB Drake Maye #3 overall',
      'Hired Jerod Mayo as head coach, replacing Bill Belichick',
      'Hired Alex Van Pelt as offensive coordinator',
      'Extended RB Rhamondre Stevenson, S Kyle Dugger, DT Christian Barmore',
    ],
    offensiveOutlook: 'Focus on establishing a physical ground game with Stevenson while developing Drake Maye.',
    defensiveOutlook: 'Top-10 defensive unit returning Christian Gonzalez and Matthew Judon/Barmore front.',
    headCoach: 'Jerod Mayo (OC: Alex Van Pelt)',
    keyPlaymakers: ['Drake Maye (QB)', 'Rhamondre Stevenson (RB)', 'Christian Gonzalez (CB)', 'Kyle Dugger (S)'],
  },
  NYJ: {
    abbr: 'NYJ',
    city: 'New York',
    name: 'Jets',
    lastSeasonRecord: '5-12',
    lastSeasonFinish: '3rd in AFC East',
    expectations2026: 'All-in win-now roster with elite top-3 NFL defense and healthy Aaron Rodgers.',
    bigPlayerMovements: [
      'Aaron Rodgers fully healthy following Achilles recovery',
      'Traded for edge-rusher Haason Reddick from Eagles',
      'Signed WR Mike Williams (ex-Chargers) and LT Tyron Smith',
      'Drafted OT Olu Fashanu in Round 1 to solidify protection',
    ],
    offensiveOutlook: 'Rodgers distributing to Breece Hall and Garrett Wilson behind an upgraded offensive line.',
    defensiveOutlook: 'Elite defense led by Sauce Gardner, Quinnen Williams, and C.J. Mosley.',
    headCoach: 'Robert Saleh (OC: Nathaniel Hackett)',
    keyPlaymakers: ['Aaron Rodgers (QB)', 'Breece Hall (RB)', 'Garrett Wilson (WR)', 'Sauce Gardner (CB)'],
  },
  BAL: {
    abbr: 'BAL',
    city: 'Baltimore',
    name: 'Ravens',
    lastSeasonRecord: '12-5',
    lastSeasonFinish: '1st in AFC North · AFC Championship Game',
    expectations2026: 'Championship favorite. Two-time MVP Lamar Jackson paired with powerhouse Derrick Henry.',
    bigPlayerMovements: [
      'Signed superstar RB Derrick Henry to headline the rushing attack',
      'Drafted ballhawk CB Nate Wiggins #30 overall',
      'Promoted Zach Orr to defensive coordinator after Mike Macdonald departure',
      'Key departures: LB Patrick Queen (to Steelers), OT Morgan Moses',
    ],
    offensiveOutlook: 'Terrifying backfield pairing of Lamar Jackson and Derrick Henry with TE Mark Andrews and Zay Flowers.',
    defensiveOutlook: 'Roquan Smith and Kyle Hamilton orchestrate a suffocating turnover defense.',
    headCoach: 'John Harbaugh (OC: Todd Monken)',
    keyPlaymakers: ['Lamar Jackson (QB)', 'Derrick Henry (RB)', 'Roquan Smith (LB)', 'Kyle Hamilton (S)'],
  },
  CIN: {
    abbr: 'CIN',
    city: 'Cincinnati',
    name: 'Bengals',
    lastSeasonRecord: '9-8',
    lastSeasonFinish: '4th in AFC North (Joe Burrow missed 7 games)',
    expectations2026: 'Super Bowl ceiling with fully healthy Joe Burrow and Ja\'Marr Chase.',
    bigPlayerMovements: [
      'Joe Burrow returns at 100% after wrist ligament reconstruction',
      'Franchise tagged Tee Higgins to preserve elite WR duo',
      'Signed OT Trent Brown and drafted OT Amarius Mims',
      'Replaced Joe Mixon with Zack Moss in the backfield',
    ],
    offensiveOutlook: 'High-volume aerial assault with Burrow, Chase, and Higgins capable of 30+ points weekly.',
    defensiveOutlook: 'Trey Hendrickson anchors pass-rush; secondary features Dax Hill and Geno Stone.',
    headCoach: 'Zac Taylor (DC: Lou Anarumo)',
    keyPlaymakers: ['Joe Burrow (QB)', 'Ja\'Marr Chase (WR)', 'Tee Higgins (WR)', 'Trey Hendrickson (DE)'],
  },
  CLE: {
    abbr: 'CLE',
    city: 'Cleveland',
    name: 'Browns',
    lastSeasonRecord: '3-14',
    lastSeasonFinish: '3rd in AFC North',
    expectations2026: 'Dominant defensive powerhouse led by DPOY Myles Garrett; QB play dictates playoff ceiling.',
    bigPlayerMovements: [
      'Traded for WR Jerry Jeudy from Denver and signed him to extension',
      'Hired Ken Dorsey as offensive coordinator to modernize passing game',
      'Nick Chubb rehabbing knee for mid-season return',
      'Signed backup QB Jameis Winston for high-floor insurance',
    ],
    offensiveOutlook: 'Ken Dorsey spread concepts with Amari Cooper, Jerry Jeudy, and David Njoku.',
    defensiveOutlook: 'Jim Schwartz defense led by Myles Garrett, Jeremiah Owusu-Koramoah, and Denzel Ward.',
    headCoach: 'Kevin Stefanski (DC: Jim Schwartz)',
    keyPlaymakers: ['Myles Garrett (DE)', 'Amari Cooper (WR)', 'David Njoku (TE)', 'Denzel Ward (CB)'],
  },
  PIT: {
    abbr: 'PIT',
    city: 'Pittsburgh',
    name: 'Steelers',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '3rd in AFC North · AFC Wild Card',
    expectations2026: 'Mike Tomlin has never had a losing season in 17 years. High floor with rebuilt QB room.',
    bigPlayerMovements: [
      'Signed Super Bowl champion QB Russell Wilson',
      'Traded for QB Justin Fields from Chicago Bears',
      'Signed premier ILB Patrick Queen away from Baltimore',
      'Traded WR Diontae Johnson to Carolina for CB Donte Jackson',
    ],
    offensiveOutlook: 'Arthur Smith run-heavy scheme featuring Najee Harris, Jaylen Warren, and George Pickens.',
    defensiveOutlook: 'Fearsome front seven led by T.J. Watt, Alex Highsmith, Patrick Queen, and Cam Heyward.',
    headCoach: 'Mike Tomlin (OC: Arthur Smith)',
    keyPlaymakers: ['T.J. Watt (LB)', 'George Pickens (WR)', 'Minkah Fitzpatrick (S)', 'Patrick Queen (LB)'],
  },
  HOU: {
    abbr: 'HOU',
    city: 'Houston',
    name: 'Texans',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '1st in AFC South · AFC Divisional Round',
    expectations2026: 'Breakout AFC contender. C.J. Stroud enters Year 2 surrounded by premier weapons.',
    bigPlayerMovements: [
      'Blockbuster trade for 4x Pro Bowl WR Stefon Diggs',
      'Acquired RB Joe Mixon from Bengals and signed him to extension',
      'Signed premier pass rusher Danielle Hunter (ex-Vikings)',
      'Signed veteran defensive linemen Denico Autry and Azeez Al-Shaair',
    ],
    offensiveOutlook: 'Dynamic Bobby Slowik attack with Stroud, Diggs, Nico Collins, Tank Dell, and Joe Mixon.',
    defensiveOutlook: 'DeMeco Ryans relentless pass-rush with Will Anderson Jr. and Danielle Hunter off edges.',
    headCoach: 'DeMeco Ryans (OC: Bobby Slowik)',
    keyPlaymakers: ['C.J. Stroud (QB)', 'Stefon Diggs (WR)', 'Nico Collins (WR)', 'Danielle Hunter (DE)'],
  },
  IND: {
    abbr: 'IND',
    city: 'Indianapolis',
    name: 'Colts',
    lastSeasonRecord: '8-9',
    lastSeasonFinish: '3rd in AFC South',
    expectations2026: 'Explosive dual-threat potential under Shane Steichen with healthy Anthony Richardson.',
    bigPlayerMovements: [
      'QB Anthony Richardson fully recovered from rookie shoulder surgery',
      'Drafted premier pass rusher Laiatu Latu #15 overall',
      'Re-signed WR Michael Pittman Jr. and DT Grover Stewart',
      'Drafted explosive WR Adonai Mitchell in Round 2',
    ],
    offensiveOutlook: 'Devastating read-option and power run game with Richardson and Jonathan Taylor.',
    defensiveOutlook: 'DeForest Buckner and Laiatu Latu anchor an aggressive front seven.',
    headCoach: 'Shane Steichen (DC: Gus Bradley)',
    keyPlaymakers: ['Anthony Richardson (QB)', 'Jonathan Taylor (RB)', 'Michael Pittman Jr. (WR)', 'DeForest Buckner (DT)'],
  },
  JAX: {
    abbr: 'JAX',
    city: 'Jacksonville',
    name: 'Jaguars',
    lastSeasonRecord: '4-13',
    lastSeasonFinish: '4th in AFC South',
    expectations2026: 'High-upside bounce-back candidate with Trevor Lawrence locked into a $275M extension.',
    bigPlayerMovements: [
      'Signed Trevor Lawrence to massive 5-year extension',
      'Drafted lightning-fast LSU WR Brian Thomas Jr. in Round 1',
      'Signed WR Gabe Davis and DT Arik Armstead from 49ers',
      'Hired defensive coordinator Ryan Nielsen to revamp the scheme',
    ],
    offensiveOutlook: 'Doug Pederson vertical passing offense with Brian Thomas Jr., Christian Kirk, and Travis Etienne.',
    defensiveOutlook: 'Josh Hines-Allen and Travon Walker form a formidable 25+ sack edge tandem.',
    headCoach: 'Doug Pederson (DC: Ryan Nielsen)',
    keyPlaymakers: ['Trevor Lawrence (QB)', 'Travis Etienne Jr. (RB)', 'Josh Hines-Allen (DE)', 'Christian Kirk (WR)'],
  },
  TEN: {
    abbr: 'TEN',
    city: 'Tennessee',
    name: 'Titans',
    lastSeasonRecord: '3-14',
    lastSeasonFinish: '4th in AFC South',
    expectations2026: 'Modernized offensive overhaul under head coach Brian Callahan and big-armed Will Levis.',
    bigPlayerMovements: [
      'Hired head coach Brian Callahan (ex-Bengals OC)',
      'Signed star WR Calvin Ridley to $92M mega-deal',
      'Signed RB Tony Pollard, WR Tyler Boyd, and C Lloyd Cushenberry',
      'Drafted OT JC Latham #7 overall and traded for CB L\'Jarius Sneed',
    ],
    offensiveOutlook: 'Transitioning from power run to explosive vertical passing with Levis, Ridley, and Hopkins.',
    defensiveOutlook: 'Jeffery Simmons dominates inside; L\'Jarius Sneed locks down opposing #1 receivers.',
    headCoach: 'Brian Callahan (DC: Dennard Wilson)',
    keyPlaymakers: ['Will Levis (QB)', 'Calvin Ridley (WR)', 'Jeffery Simmons (DT)', 'L\'Jarius Sneed (CB)'],
  },
  DEN: {
    abbr: 'DEN',
    city: 'Denver',
    name: 'Broncos',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '2nd in AFC West',
    expectations2026: 'Sean Payton disciplined ball-control system featuring accurate rookie QB Bo Nix.',
    bigPlayerMovements: [
      'Drafted Oregon QB Bo Nix #12 overall as the new franchise signal caller',
      'Moved on from Russell Wilson (eating historic dead cap)',
      'Traded WR Jerry Jeudy to Cleveland for draft picks',
      'Signed safety Brandon Jones and extended Patrick Surtain II',
    ],
    offensiveOutlook: 'Payton West Coast quick-game system maximizing Nix\'s lightning release and processing.',
    defensiveOutlook: 'All-Pro corner Patrick Surtain II erases opponent top WRs; active front seven.',
    headCoach: 'Sean Payton (DC: Vance Joseph)',
    keyPlaymakers: ['Patrick Surtain II (CB)', 'Courtland Sutton (WR)', 'Bo Nix (QB)', 'Javonte Williams (RB)'],
  },
  KC: {
    abbr: 'KC',
    city: 'Kansas City',
    name: 'Chiefs',
    lastSeasonRecord: '15-2',
    lastSeasonFinish: '1st in AFC West · Super Bowl Champions 🏆',
    expectations2026: 'Super Bowl three-peat favorites. Patrick Mahomes + Andy Reid remains the gold standard.',
    bigPlayerMovements: [
      'Drafted fastest 40-yard dash record-holder Xavier Worthy (4.21s)',
      'Signed vertical speedster WR Marquise "Hollywood" Brown',
      'Re-signed All-Pro DT Chris Jones to record extension',
      'Traded CB L\'Jarius Sneed to Tennessee',
    ],
    offensiveOutlook: 'Restored deep-ball capability for Mahomes alongside Travis Kelce, Worthy, and Isiah Pacheco.',
    defensiveOutlook: 'Steve Spagnuolo\'s exotic blitz packages anchored by dominant tackle Chris Jones.',
    headCoach: 'Andy Reid (DC: Steve Spagnuolo)',
    keyPlaymakers: ['Patrick Mahomes (QB)', 'Travis Kelce (TE)', 'Chris Jones (DT)', 'Isiah Pacheco (RB)'],
  },
  LAC: {
    abbr: 'LAC',
    city: 'Los Angeles',
    name: 'Chargers',
    lastSeasonRecord: '11-6',
    lastSeasonFinish: '2nd in AFC West · AFC Wild Card',
    expectations2026: 'Major cultural transformation under Jim Harbaugh. Elite QB Justin Herbert with physical identity.',
    bigPlayerMovements: [
      'Hired national champion head coach Jim Harbaugh',
      'Drafted mauler OT Joe Alt #5 overall to pair with Rashawn Slater',
      'Moved on from WRs Keenan Allen (Bears) and Mike Williams (Jets)',
      'Signed physical RBs Gus Edwards and J.K. Dobbins from Baltimore',
    ],
    offensiveOutlook: 'Harbaugh\'s smashmouth run-first philosophy setting up deep play-action shots for Herbert.',
    defensiveOutlook: 'Jesse Minter brings Michigan\'s dynamic scheme; Joey Bosa, Khalil Mack, and Derwin James.',
    headCoach: 'Jim Harbaugh (OC: Greg Roman)',
    keyPlaymakers: ['Justin Herbert (QB)', 'Derwin James (S)', 'Khalil Mack (LB)', 'Joey Bosa (DE)'],
  },
  LV: {
    abbr: 'LV',
    city: 'Las Vegas',
    name: 'Raiders',
    lastSeasonRecord: '4-13',
    lastSeasonFinish: '4th in AFC West',
    expectations2026: 'Fierce, hard-hitting defensive identity under Antonio Pierce; QB play is key variable.',
    bigPlayerMovements: [
      'Hired Antonio Pierce as full-time head coach after energetic interim run',
      'Signed elite DT Christian Wilkins to massive $110M contract',
      'Drafted generational TE Brock Bowers #13 overall',
      'Signed QB Gardner Minshew to compete with Aidan O\'Connell',
    ],
    offensiveOutlook: 'Davante Adams and rookie hybrid TE Brock Bowers anchor the passing game with Zamir White running.',
    defensiveOutlook: 'Terrifying interior-edge combo of Maxx Crosby and Christian Wilkins.',
    headCoach: 'Antonio Pierce (OC: Luke Getsy)',
    keyPlaymakers: ['Maxx Crosby (DE)', 'Davante Adams (WR)', 'Christian Wilkins (DT)', 'Brock Bowers (TE)'],
  },
  DAL: {
    abbr: 'DAL',
    city: 'Dallas',
    name: 'Cowboys',
    lastSeasonRecord: '7-10',
    lastSeasonFinish: '2nd in NFC East',
    expectations2026: 'High regular-season win floor. Dak Prescott + CeeDee Lamb + Micah Parsons form elite core.',
    bigPlayerMovements: [
      'Signed WR CeeDee Lamb and QB Dak Prescott to record extensions',
      'Re-signed veteran RB Ezekiel Elliott to share backfield',
      'Drafted OT Tyler Guyton in Round 1 to replace Tyron Smith',
      'Hired Mike Zimmer as defensive coordinator replacing Dan Quinn',
    ],
    offensiveOutlook: 'Mike McCarthy passing game feeding high-volume targets to CeeDee Lamb and Jake Ferguson.',
    defensiveOutlook: 'Mike Zimmer scheme featuring DPOY frontrunner Micah Parsons and ballhawk Trevon Diggs.',
    headCoach: 'Mike McCarthy (DC: Mike Zimmer)',
    keyPlaymakers: ['CeeDee Lamb (WR)', 'Micah Parsons (LB)', 'Dak Prescott (QB)', 'Trevon Diggs (CB)'],
  },
  NYG: {
    abbr: 'NYG',
    city: 'New York',
    name: 'Giants',
    lastSeasonRecord: '3-14',
    lastSeasonFinish: '4th in NFC East',
    expectations2026: 'Rebuilt trenches and electric rookie weapon Malik Nabers give Giants higher ceiling.',
    bigPlayerMovements: [
      'Drafted superstar WR Malik Nabers #6 overall',
      'Traded for and extended elite edge-rusher Brian Burns from Carolina',
      'Lost Saquon Barkley to division rival Philadelphia',
      'Upgraded OL with Jon Runyan Jr. and Jermaine Eluemunor',
    ],
    offensiveOutlook: 'Brian Daboll schemes targets for game-breaker Malik Nabers and Wan\'Dale Robinson.',
    defensiveOutlook: 'Lethal pass-rush trio: Dexter Lawrence, Brian Burns, and Kayvon Thibodeaux.',
    headCoach: 'Brian Daboll (DC: Shane Bowen)',
    keyPlaymakers: ['Malik Nabers (WR)', 'Dexter Lawrence (DT)', 'Brian Burns (DE)', 'Kayvon Thibodeaux (LB)'],
  },
  PHI: {
    abbr: 'PHI',
    city: 'Philadelphia',
    name: 'Eagles',
    lastSeasonRecord: '14-3',
    lastSeasonFinish: '1st in NFC East · NFC Divisional Round',
    expectations2026: 'Top-tier Super Bowl contender. Saquon Barkley adds unmatched explosiveness to Jalen Hurts.',
    bigPlayerMovements: [
      'Signed superstar RB Saquon Barkley to blockbuster 3-year contract',
      'Hired veteran coordinators Kellen Moore (OC) and Vic Fangio (DC)',
      'Drafted top corners Quinyon Mitchell and Cooper DeJean in Rounds 1-2',
      'Key departures: Center Jason Kelce and DT Fletcher Cox (retired)',
    ],
    offensiveOutlook: 'Kellen Moore motion-heavy attack with Jalen Hurts, Saquon Barkley, A.J. Brown, and DeVonta Smith.',
    defensiveOutlook: 'Vic Fangio scheme with overhauled athletic secondary and Jalen Carter dominating inside.',
    headCoach: 'Nick Sirianni (OC: Kellen Moore, DC: Vic Fangio)',
    keyPlaymakers: ['Jalen Hurts (QB)', 'Saquon Barkley (RB)', 'A.J. Brown (WR)', 'Jalen Carter (DT)'],
  },
  WSH: {
    abbr: 'WSH',
    city: 'Washington',
    name: 'Commanders',
    lastSeasonRecord: '12-5',
    lastSeasonFinish: '2nd in NFC East · NFC Wild Card',
    expectations2026: 'Surging franchise energized by Heisman dual-threat QB Jayden Daniels and coach Dan Quinn.',
    bigPlayerMovements: [
      'Drafted Heisman winner QB Jayden Daniels #2 overall',
      'Hired head coach Dan Quinn and offensive coordinator Kliff Kingsbury',
      'Aggressive free agency: signed Frankie Luvu, Bobby Wagner, Austin Ekeler',
      'Traded WR Jahan Dotson to Philadelphia',
    ],
    offensiveOutlook: 'Kliff Kingsbury uptempo spread showcasing Jayden Daniels\' laser arm and electric scrambling.',
    defensiveOutlook: 'Dan Quinn and Joe Whitt Jr. install fast, turnover-minded physical defense.',
    headCoach: 'Dan Quinn (OC: Kliff Kingsbury)',
    keyPlaymakers: ['Jayden Daniels (QB)', 'Terry McLaurin (WR)', 'Frankie Luvu (LB)', 'Bobby Wagner (LB)'],
  },
  CHI: {
    abbr: 'CHI',
    city: 'Chicago',
    name: 'Bears',
    lastSeasonRecord: '5-12',
    lastSeasonFinish: '4th in NFC North',
    expectations2026: 'One of the most hyped rosters in the NFL with generational QB Caleb Williams and loaded weapons.',
    bigPlayerMovements: [
      'Drafted generational QB Caleb Williams #1 overall',
      'Drafted explosive WR Rome Odunze #9 overall',
      'Traded for 6x Pro Bowl WR Keenan Allen from Chargers',
      'Signed dynamic RB D\'Andre Swift and hired OC Shane Waldron',
    ],
    offensiveOutlook: 'Unstoppable skill group: Caleb Williams throwing to DJ Moore, Keenan Allen, Rome Odunze, Cole Kmet.',
    defensiveOutlook: 'Top-5 defense over second half of last season anchored by Montez Sweat and Jaylon Johnson.',
    headCoach: 'Matt Eberflus (OC: Shane Waldron)',
    keyPlaymakers: ['Caleb Williams (QB)', 'DJ Moore (WR)', 'Montez Sweat (DE)', 'Jaylon Johnson (CB)'],
  },
  DET: {
    abbr: 'DET',
    city: 'Detroit',
    name: 'Lions',
    lastSeasonRecord: '15-2',
    lastSeasonFinish: '1st in NFC North · NFC Championship Game',
    expectations2026: 'Prime Super Bowl contender. Best offensive line in football with relentless Dan Campbell grit.',
    bigPlayerMovements: [
      'Extended QB Jared Goff, WR Amon-Ra St. Brown, and OT Penei Sewell',
      'Traded up to draft shutdown CB Terrion Arnold in Round 1',
      'Signed elite run-stuffing DT D.J. Reader and pass-rusher Marcus Davenport',
      'Retained top coordinators Ben Johnson and Aaron Glenn',
    ],
    offensiveOutlook: 'Ben Johnson masterclass: Goff dealing to St. Brown, Sam LaPorta, Jahmyr Gibbs, and David Montgomery.',
    defensiveOutlook: 'Aidan Hutchinson leads aggressive pass rush; overhauled secondary erases last year\'s weakness.',
    headCoach: 'Dan Campbell (OC: Ben Johnson)',
    keyPlaymakers: ['Amon-Ra St. Brown (WR)', 'Aidan Hutchinson (DE)', 'Jahmyr Gibbs (RB)', 'Penei Sewell (OT)'],
  },
  GB: {
    abbr: 'GB',
    city: 'Green Bay',
    name: 'Packers',
    lastSeasonRecord: '11-6',
    lastSeasonFinish: '2nd in NFC North · NFC Divisional Round',
    expectations2026: 'Ascending NFC powerhouse. Jordan Love showed MVP-caliber play down the stretch.',
    bigPlayerMovements: [
      'Signed Jordan Love to massive 4-year $220M extension',
      'Signed former rushing champion Josh Jacobs (ex-Raiders)',
      'Signed ballhawk safety Xavier McKinney from NY Giants',
      'Hired Jeff Hafley as defensive coordinator, switching to aggressive 4-3',
    ],
    offensiveOutlook: 'Matt LaFleur creative scheme with Love distributing to Christian Watson, Jayden Reed, Romeo Doubs, and Jacobs.',
    defensiveOutlook: 'Jeff Hafley fast-attacking front with Rashan Gary, Kenny Clark, and Xavier McKinney.',
    headCoach: 'Matt LaFleur (DC: Jeff Hafley)',
    keyPlaymakers: ['Jordan Love (QB)', 'Josh Jacobs (RB)', 'Rashan Gary (LB)', 'Xavier McKinney (S)'],
  },
  MIN: {
    abbr: 'MIN',
    city: 'Minnesota',
    name: 'Vikings',
    lastSeasonRecord: '14-3',
    lastSeasonFinish: '2nd in NFC North',
    expectations2026: 'Premier offensive weapons led by Justin Jefferson and Brian Flores\' league-leading exotic blitz defense.',
    bigPlayerMovements: [
      'Signed Justin Jefferson to record $140M receiver extension',
      'Drafted QB J.J. McCarthy in Round 1 and signed veteran Sam Darnold',
      'Signed Pro Bowl RB Aaron Jones away from Green Bay',
      'Signed pass-rushers Jonathan Greenard and Andrew Van Ginkel',
    ],
    offensiveOutlook: 'Kevin O\'Connell scheme maximizing Justin Jefferson, Jordan Addison, and Aaron Jones.',
    defensiveOutlook: 'Brian Flores runs the NFL\'s most disguised coverage and blitz packages.',
    headCoach: 'Kevin O\'Connell (DC: Brian Flores)',
    keyPlaymakers: ['Justin Jefferson (WR)', 'Aaron Jones (RB)', 'Jonathan Greenard (DE)', 'Harrison Smith (S)'],
  },
  ATL: {
    abbr: 'ATL',
    city: 'Atlanta',
    name: 'Falcons',
    lastSeasonRecord: '8-9',
    lastSeasonFinish: '2nd in NFC South',
    expectations2026: 'Heavy favorites to win NFC South with softest schedule in the NFL and Kirk Cousins at QB.',
    bigPlayerMovements: [
      'Signed 4x Pro Bowl QB Kirk Cousins to $180M deal',
      'Hired head coach Raheem Morris and OC Zac Robinson (ex-Rams)',
      'Drafted QB Michael Penix Jr. #8 overall as future successor',
      'Traded for edge-rusher Matthew Judon and signed safety Justin Simmons',
    ],
    offensiveOutlook: 'Sean McVay system unlocking Bijan Robinson, Drake London, and Kyle Pitts.',
    defensiveOutlook: 'Raheem Morris and Jimmy Lake defense bolstered by Matthew Judon, Justin Simmons, and Jessie Bates III.',
    headCoach: 'Raheem Morris (OC: Zac Robinson)',
    keyPlaymakers: ['Bijan Robinson (RB)', 'Kirk Cousins (QB)', 'Drake London (WR)', 'Jessie Bates III (S)'],
  },
  CAR: {
    abbr: 'CAR',
    city: 'Carolina',
    name: 'Panthers',
    lastSeasonRecord: '5-12',
    lastSeasonFinish: '4th in NFC South',
    expectations2026: 'Bargain-bin value play. Dave Canales brings QB revival playbook for Bryce Young.',
    bigPlayerMovements: [
      'Hired head coach Dave Canales (credited with Geno Smith & Baker Mayfield revivals)',
      'Invested $153M in offensive line: signed Robert Hunt and Damien Lewis',
      'Traded for separation specialist WR Diontae Johnson',
      'Drafted explosive rookie WR Xavier Legette in Round 1',
    ],
    offensiveOutlook: 'Canales quick-read West Coast scheme to protect and elevate Bryce Young.',
    defensiveOutlook: 'Ejiro Evero scheme led by All-Pro DT Derrick Brown, despite losing Brian Burns.',
    headCoach: 'Dave Canales (DC: Ejiro Evero)',
    keyPlaymakers: ['Bryce Young (QB)', 'Derrick Brown (DT)', 'Diontae Johnson (WR)', 'Chuba Hubbard (RB)'],
  },
  NO: {
    abbr: 'NO',
    city: 'New Orleans',
    name: 'Saints',
    lastSeasonRecord: '5-12',
    lastSeasonFinish: '3rd in NFC South',
    expectations2026: 'Solid veteran defense; new offensive coordinator Klint Kubiak brings 49ers-style outside zone.',
    bigPlayerMovements: [
      'Hired OC Klint Kubiak from 49ers coaching staff',
      'Drafted physical OT Taliese Fuaga #14 overall to fortify line',
      'Signed defensive end Chase Young to boost pass-rush',
      'Alvin Kamara and Chris Olave remain primary focal points',
    ],
    offensiveOutlook: 'Kubiak play-action and outside zone attack utilizing Kamara, Olave, and Rashid Shaheed.',
    defensiveOutlook: 'Dennis Allen defense remains solid with Demario Davis, Cam Jordan, Tyrann Mathieu, and Marshon Lattimore.',
    headCoach: 'Dennis Allen (OC: Klint Kubiak)',
    keyPlaymakers: ['Alvin Kamara (RB)', 'Chris Olave (WR)', 'Demario Davis (LB)', 'Rashid Shaheed (WR)'],
  },
  TB: {
    abbr: 'TB',
    city: 'Tampa Bay',
    name: 'Buccaneers',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '1st in NFC South · NFC Divisional Round',
    expectations2026: 'Defending NFC South champions with proven chemistry and favorable divisional slate.',
    bigPlayerMovements: [
      'Re-signed QB Baker Mayfield and WR Mike Evans to multi-year deals',
      'Made Antoine Winfield Jr. the highest-paid safety in NFL history',
      'Drafted standout center Graham Barton in Round 1',
      'Hired Liam Coen as offensive coordinator (ex-Rams)',
    ],
    offensiveOutlook: 'Baker Mayfield delivering to Evans, Chris Godwin, and emerging back Rachaad White.',
    defensiveOutlook: 'Todd Bowles aggressive blitzing defense led by Winfield, Vita Vea, and Lavonte David.',
    headCoach: 'Todd Bowles (OC: Liam Coen)',
    keyPlaymakers: ['Baker Mayfield (QB)', 'Mike Evans (WR)', 'Antoine Winfield Jr. (S)', 'Vita Vea (DT)'],
  },
  ARI: {
    abbr: 'ARI',
    city: 'Arizona',
    name: 'Cardinals',
    lastSeasonRecord: '8-9',
    lastSeasonFinish: '3rd in NFC West',
    expectations2026: 'Dark horse playoff contender. Fully healthy Kyler Murray paired with generational WR Marvin Harrison Jr.',
    bigPlayerMovements: [
      'Drafted generational WR Marvin Harrison Jr. #4 overall',
      'Signed defensive linemen Justin Jones, Bilal Nichols, and tackle Jonah Williams',
      'Drafted CB Max Melton and RB Trey Benson',
      'Kyler Murray has full offseason in Drew Petzing\'s offensive system',
    ],
    offensiveOutlook: 'Top-10 offensive upside with Murray, Harrison Jr., TE Trey McBride, and power back James Conner.',
    defensiveOutlook: 'Jonathan Gannon and Nick Rallis get maximum effort from young, disciplined roster.',
    headCoach: 'Jonathan Gannon (OC: Drew Petzing)',
    keyPlaymakers: ['Kyler Murray (QB)', 'Marvin Harrison Jr. (WR)', 'Trey McBride (TE)', 'Budda Baker (S)'],
  },
  LAR: {
    abbr: 'LAR',
    city: 'Los Angeles',
    name: 'Rams',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '2nd in NFC West · NFC Wild Card',
    expectations2026: 'Potent Sean McVay offense with lethal receiver tandem Puka Nacua and Cooper Kupp.',
    bigPlayerMovements: [
      'Legendary DT Aaron Donald retired; drafted Jared Verse and Braden Fiske to replace him',
      'Invested heavily in interior OL: signed Jonah Jackson and re-signed Kevin Dotson',
      'Re-signed veteran CB Darious Williams and safety Kamren Curl',
      'Signed backup RB Blake Corum in draft',
    ],
    offensiveOutlook: 'Matthew Stafford directing McVay passing machine with Puka Nacua, Cooper Kupp, and Kyren Williams.',
    defensiveOutlook: 'Youth movement on defense led by Kobie Turner, Byron Young, and rookie Jared Verse.',
    headCoach: 'Sean McVay (DC: Chris Shula)',
    keyPlaymakers: ['Matthew Stafford (QB)', 'Puka Nacua (WR)', 'Cooper Kupp (WR)', 'Kyren Williams (RB)'],
  },
  SF: {
    abbr: 'SF',
    city: 'San Francisco',
    name: '49ers',
    lastSeasonRecord: '6-11',
    lastSeasonFinish: '4th in NFC West (Injuries)',
    expectations2026: 'Elite bounce-back Super Bowl contender. Most complete roster in football when healthy.',
    bigPlayerMovements: [
      'Signed superstar WR Brandon Aiyuk and OT Trent Williams to massive extensions',
      'Christian McCaffrey returns as undisputed #1 offensive weapon',
      'Signed edge-rusher Leonard Floyd and LB De\'Vondre Campbell',
      'Drafted WR Ricky Pearsall in Round 1',
    ],
    offensiveOutlook: 'Kyle Shanahan unmatched scheme: Purdy, McCaffrey, Deebo Samuel, George Kittle, Brandon Aiyuk.',
    defensiveOutlook: 'Nick Bosa and Fred Warner anchor a perennial top-5 defensive unit.',
    headCoach: 'Kyle Shanahan (DC: Nick Sorensen)',
    keyPlaymakers: ['Christian McCaffrey (RB)', 'Brock Purdy (QB)', 'Nick Bosa (DE)', 'Fred Warner (LB)'],
  },
  SEA: {
    abbr: 'SEA',
    city: 'Seattle',
    name: 'Seahawks',
    lastSeasonRecord: '10-7',
    lastSeasonFinish: '3rd in NFC West',
    expectations2026: 'Dangerous playoff contender with Mike Macdonald installing Baltimore\'s championship defense.',
    bigPlayerMovements: [
      'Hired defensive mastermind Mike Macdonald as head coach (ex-Ravens DC)',
      'Hired Ryan Grubb as OC (architect of Washington Huskies explosive offense)',
      'Drafted dominant interior DT Byron Murphy II #16 overall',
      'Re-signed Leonard Williams and signed safety Rayshawn Jenkins',
    ],
    offensiveOutlook: 'Ryan Grubb deep passing attack with Geno Smith launching to DK Metcalf, Tyler Lockett, Jaxon Smith-Njigba.',
    defensiveOutlook: 'Macdonald dynamic disguise scheme with Devon Witherspoon, Byron Murphy, and Leonard Williams.',
    headCoach: 'Mike Macdonald (OC: Ryan Grubb)',
    keyPlaymakers: ['DK Metcalf (WR)', 'Devon Witherspoon (CB)', 'Geno Smith (QB)', 'Kenneth Walker III (RB)'],
  },
};
